import React, { useMemo } from 'react';
import { ContainerShape, InnerShape, InnerShape3, InnerShape4 } from './Shape';
import { motion } from 'framer-motion';
import { icons } from '../assets/icons/icons';
import { useResolution } from '../hooks/useResolution';
import { useModal } from '~/core/contexts/ModalContext';
import { NoReferral } from '../modals/no.referral.modal';
import { useReferrer } from '../hooks/useReferral';
import { useAccount } from 'wagmi';
import { useAtomValue } from 'jotai';
import { atomHoneyEarned, atomRecentBins, atomWageredEth, atomTotalWinEth } from '../states/game.atoms';
import { formatNumber } from '~/core/utils/formatNumber';

export const RecentBins: React.FC = () => {
  const { showModal } = useModal();
  const { address } = useAccount();
  const { data: referrer, isLoading: isReferrerLoading } = useReferrer();
  
  // Atoms
  const recentBins = useAtomValue(atomRecentBins);
  const honeyEarned = useAtomValue(atomHoneyEarned);
  const totalWinEth = useAtomValue(atomTotalWinEth);
  const wageredEth = useAtomValue(atomWageredEth);

  const filteredBins = useMemo(() => recentBins.filter(bin => bin !== '0'), [recentBins]);
  
  const totalMultiplier = useMemo(() => {
    if (wageredEth === 0n) return 0;
    const scaled = (totalWinEth * 10000n) / wageredEth;
    return Number(scaled) / 10000;
  }, [totalWinEth, wageredEth]);

  const totalMultiplierDisplay = useMemo(
    () => `${totalMultiplier.toFixed(2)}x`,
    [totalMultiplier]
  );

  const { isMobile } = useResolution();
  const shouldShowReferralCta = Boolean(address) && !isReferrerLoading && !referrer;

  const onOpenReferral = () => {
    showModal(hide => <NoReferral onDismiss={hide} />, { hideOnEscapeKey: true });
  }

  if (isMobile) {
    return (
      <div className="flex flex-col">
        <div className='flex items-center justify-center' style={{ transform: 'translateY(1px)' }}>
          <div className="rounded rounded-b-none" style={{ border: '2px solid #FFF085'}}>
            <div className="px-4 py-1 bg-[#F0B10080]">
              <p className="text-[10px] font-blinko text-white">
                HONEY EARNED
              </p>
            </div>
          </div>
        </div>

        <ContainerShape cornerDiagonal={20} cornerRadius={12} strokeWidth={2}>
          <div className='p-4 pb-2'>
            <div className={`relative`}>
              {shouldShowReferralCta ? (
                <div className="flex flex-col gap-2 w-[200px]">
                  <InnerShape cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                    <div className="flex items-center gap-2 px-3 py-2">
                      <p className="text-[10px] font-blinko text-white flex-1 text-left">
                        Need a referral code to earn honey
                      </p>
                      <button onPointerDown={onOpenReferral}>
                        <InnerShape3 cornerDiagonal={8} cornerRadius={4} strokeWidth={1}>
                          <div className="px-2 py-1">
                            <p className="text-[10px] font-blinko text-white">Enter</p>
                          </div>
                        </InnerShape3>
                      </button>
                    </div>
                  </InnerShape>
                </div>
              ) : (
                <div className="flex flex-col gap-2 w-[160px]">
                  <motion.div>
                    <InnerShape4 cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                      <div className="flex flex-col justify-center items-center gap-1.5">
                        <p className="text-lg font-blinko text-white">
                          {formatNumber(honeyEarned)}
                        </p>
                      </div>
                    </InnerShape4>
                  </motion.div>
                </div>
              )}

            <div className="mt-2">
              <div className="flex justify-center items-center gap-1">
                <p className="text-[10px] font-blinko text-white">
                  {totalMultiplierDisplay}
                </p>
              </div>
            </div>
            </div>
          </div>
        </ContainerShape>
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      <div className='flex items-center justify-center' style={{ transform: 'translateY(1px)' }}>
        <div className="rounded rounded-b-none" style={{ border: '2px solid #FFF085'}}>
          <div className="px-4 py-1 bg-[#F0B10080]">
            <p className="text-[10px] font-blinko text-white">
              Recent Results
            </p>
          </div>
        </div>
      </div>

      <ContainerShape cornerDiagonal={20} cornerRadius={12} strokeWidth={2}>
        <div className='p-4 max-h-[400px] overflow-auto'>
          <div>
            {shouldShowReferralCta ? (
              <div className="w-[200px]">
                <InnerShape cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                  <div className="flex flex-col items-center gap-2 py-4">
                    <p className="text-base font-blinko text-white flex-1 text-center">
                      Need a referral code to earn honey
                    </p>
                    <button onPointerDown={onOpenReferral}>
                      <InnerShape3 cornerDiagonal={8} cornerRadius={4} strokeWidth={1}>
                        <div className="px-3 py-2">
                          <p className="text-xs font-blinko text-white">Enter</p>
                        </div>
                      </InnerShape3>
                    </button>
                  </div>
                </InnerShape>
              </div>
            ) : (
              <div className="flex flex-col gap-2 w-[200px]">
                <motion.div>
                  <InnerShape4 cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                    <div className="flex flex-col justify-center items-center pt-4 pb-3 gap-1.5">
                      <p className="text-[30px] font-blinko text-white">
                        {formatNumber(honeyEarned)}
                      </p>
                      <p className="flex items-center gap-1 text-sm font-blinko text-white">
                        <img className='w-4 h-4' src={icons.jars} /> HONEY EARNED
                      </p>
                    </div>
                  </InnerShape4>
                </motion.div>
              </div>
            )}

          <div className="flex flex-col gap-2 w-[200px] mt-2">
            <motion.div>
              <InnerShape3 cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                <div className="flex justify-center items-center gap-2 h-8">
                  <p className="text-xs font-blinko text-white">
                    {totalMultiplierDisplay}
                  </p>
                </div>
              </InnerShape3>
            </motion.div>
          </div>

          {filteredBins.length > 0 && (
            <>
              <p className="text-center text-xs font-blinko text-white opacity-90 mt-2">
                MULTIPLIERS
              </p>

              <div className="flex flex-col gap-2 w-[200px] mt-2">
                {filteredBins.map((bin, idx) => {
                  return (
                    <motion.div
                      key={idx}
                    >
                      <InnerShape cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                        <div className="flex justify-center items-center h-8">
                          <p className="text-base font-blinko text-white">
                            {bin}<span className="text-xs">x</span>
                          </p>
                        </div>
                      </InnerShape>
                    </motion.div>
                  );
                })}
              </div>
            </>
          )}
          </div>
        </div>
      </ContainerShape>
    </div>
  )
};
